import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Skeleton, Typography, useSnackbar } from 'fronton-react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { AxiosError } from 'axios';
import { useRecoilState, useRecoilValue } from 'recoil';
import { getCookie } from 'utils/cookie.helper';
import { Snackbar, SNACKBARS_DICTIONARY, TSnackbarDictionaryKeys } from 'pages/common/components/Snackbar';
import { ParametersEnum, TInputParams } from 'pages/common/types';
import { activeRoomId, roomsAtom } from 'pages/common/store/rooms';
import { Rooms } from 'pages/common/components/Rooms';
import { DataContext } from 'entries/common/widgets/project-configuration/context';
import { gaSubmitLaminateProject } from 'ga-events/ga-submit-laminate-project';
import { TAB_LIST } from 'pages/common/consts/dimensions-tab-list';
import { measurementServiceIds } from 'pages/common/consts/services';
import { LaminateInputs } from '../../types';
import { useMeasurement } from '../../../common/hooks/use-measurement';
import { useGetRooms } from '../../hooks/use-get-rooms';
import { MeasurementBanner } from '../../components/MeasurementBanner/measurement-banner';
import {
  CUSTOMER_ID_COOKIE_NAME,
  UNAUTHORIZED_CUSTOMER_ID_COOKIE_NAME,
  userProjectConfigurationCookieNames,
  projectUpdatedCustomEventNames,
  formNames,
} from '../../const';
import { isKazakhstanRegion } from '../../helpers/is-kazakhstan-region';
import { getDefaultParams } from './helpers/get-default-params';
import { Footer } from '../../components/Footer/footer';
import { RoomsParams } from '../../components/RoomsParams/rooms-params';
import { getCreateConfigurationBody } from './helpers/get-create-configuration-body';
import { getProjectUpdatedCustomEventPayload } from './helpers/get-project-updated-custom-event-payload';
import {
  FormStyled,
  Container,
  SkeletonContainer,
  ContentContainer,
  RoomsContainerRow,
  FooterContainer,
  FooterWrapper,
  MeasurementBannerWrapper,
} from './styles';
import { getActiveTab } from './helpers/get-active-tab';
import { getFormData } from './helpers/get-form-data';
import { getRoomIdsWithSameInputParams } from './helpers/get-room-ids-with-same-input-params';
import { submitForm } from './helpers/submit-form';
import { setSelectedRoomIdIfNeeded } from './helpers/set-selected-room-id-if-needed';

const defaultInputParams: TInputParams = [ParametersEnum.length, ParametersEnum.width];

export const LaminateForm = () => {
  const data = useContext(DataContext);
  const [activeTabIndex, setActiveTabIndex] = useState(getActiveTab(data?.projectConfigurationParameters));
  const [, setInputParams] = useState(defaultInputParams);
  const { addSnack } = useSnackbar();
  const [, setSelectedRoomId] = useRecoilState(activeRoomId);
  const isAreaSelected = activeTabIndex === TAB_LIST.AREA;
  const methods = useForm<LaminateInputs>();
  const defaultValues = useMemo(() => getDefaultParams(data?.projectConfigurationParameters), [
    data?.projectConfigurationParameters,
  ]);

  useEffect(() => {
    methods.reset(defaultValues);
  }, [defaultValues, methods]);

  const { isLoading, isAvailable } = useMeasurement({
    serviceId: measurementServiceIds.LAMINATE,
    regionId: data?.regionId,
    api: data?.api,
  });

  useGetRooms();

  const calculatedRooms = useRecoilValue(roomsAtom);

  useEffect(() => {
    setSelectedRoomIdIfNeeded({
      calculatedRooms,
      defaultValues,
      isAreaSelected,
      setSelectedRoomId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Нужно послать только когда загрузились комнаты
  }, [calculatedRooms]);

  const isShowRooms = calculatedRooms.length > 0;

  const { api } = data!;

  const onSubmit: SubmitHandler<LaminateInputs> = (inputsData: LaminateInputs) => {
    const formData = getFormData({ data: inputsData, isRoomArea: isAreaSelected });
    gaSubmitLaminateProject({ params: formData });
    const roomIdsWithSameInputParams = getRoomIdsWithSameInputParams({
      dataInRooms: calculatedRooms,
      inputParams: formData,
    });
    const authorizedCustomerId = getCookie(CUSTOMER_ID_COOKIE_NAME);
    /* кука должна быть не пустой всегда,
       т.к. идентификатор яндекс метрики присваивается через куку при заходе на сайт
     */
    const unauthorizedCustomerId = getCookie(UNAUTHORIZED_CUSTOMER_ID_COOKIE_NAME) as string;
    const isAuthorized = Boolean(authorizedCustomerId);
    const customerId = isAuthorized ? (authorizedCustomerId as string) : unauthorizedCustomerId;
    const isKazakhstan = isKazakhstanRegion(data!.regionId);
    const createConfigurationBody = getCreateConfigurationBody(
      formData,
      isKazakhstan,
      customerId,
      isAuthorized
    );
    const customEventPayload = getProjectUpdatedCustomEventPayload(formData);
    submitForm({
      api,
      projectConfigurationCookieName: userProjectConfigurationCookieNames.LAMINATE,
      roomIdsWithSameInputParams,
      createConfigurationBody,
      projectUpdatedCustomEventName: projectUpdatedCustomEventNames.LAMINATE,
      projectUpdatedCustomEventPayload: customEventPayload,
    }).catch(e => {
      const status = (e as AxiosError).response?.status || '';
      addSnack(
        String(status).startsWith('4')
          ? SNACKBARS_DICTIONARY[TSnackbarDictionaryKeys.SAVE_LAMINATE_PROJECT_CLIENT_ERROR]
          : SNACKBARS_DICTIONARY[TSnackbarDictionaryKeys.SAVE_PROJECT_SERVER_ERROR]
      );
    });
  };

  return (
    <FormProvider {...methods}>
      <Container>
        <Typography variant="h1">Подбор ламината</Typography>
        {isShowRooms ? (
          <RoomsContainerRow>
            <Rooms
              setInputParams={setInputParams}
              setActiveTabIndex={setActiveTabIndex}
              defaultInputParams={defaultInputParams}
              layingTypeFieldName="layingType"
              isCalculator={false}
            />
          </RoomsContainerRow>
        ) : null}
        <ContentContainer>
          <FormStyled id={formNames.LAMINATE} onSubmit={methods.handleSubmit(onSubmit)}>
            <RoomsParams
              setInputParams={setInputParams}
              setActiveTabIndex={setActiveTabIndex}
              activeTabIndex={activeTabIndex}
            />
          </FormStyled>
          {isLoading && (
            <SkeletonContainer>
              <Skeleton variant="control" width="100%" height="100%" roundingSize="l" />
            </SkeletonContainer>
          )}
          {isAvailable && (
            <MeasurementBannerWrapper>
              <MeasurementBanner />
            </MeasurementBannerWrapper>
          )}
        </ContentContainer>
        <FooterContainer>
          <FooterWrapper>
            <Footer />
          </FooterWrapper>
        </FooterContainer>
        <Snackbar horizontalPosition="end" verticalPosition="bottom" />
      </Container>
    </FormProvider>
  );
};
